import { useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useUserContext } from '@/auth/UserContext';
import { useAbsenceCalendarRange } from '@/components/AbsenceApplication/hooks/useAbsenceCalendarRange';
import { useSubmitNonAttendanceApplication } from '@/components/AbsenceApplication/hooks/useSubmitNonAttendanceApplication';
import {
  CateringChoice,
  DateField,
  SubmitApplicationErrorCode,
  type NonAttendanceApplicationDetail,
  type NonAttendanceApplicationProperties,
  type SubmitNonAttendanceApplicationRequest,
} from '@/components/AbsenceApplication/types';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useScrollFadeProgress } from '@/hooks/useScrollFadeProgress';
import { addDays, toApiDateTime } from '@/utils/date';
import {
  ErrorMessage,
  getResponseErrorCode,
  logRequestError,
} from '@/utils/errors';
import { joinNonEmpty } from '@/utils/text';

const CALENDAR_REOPEN_DELAY_MS = 120;

interface FormValues {
  [DateField.From]: Date;
  [DateField.To]: Date | null;
  cateringChoice: CateringChoice | null;
  reason: string;
}

interface Params {
  properties: NonAttendanceApplicationProperties;
  onOverlap: () => void;
  onSuccess: (
    application: NonAttendanceApplicationDetail,
    payload: SubmitNonAttendanceApplicationRequest,
  ) => void;
  onServerError: (payload: SubmitNonAttendanceApplicationRequest) => void;
}

const normalizeEndDate = (
  startDate: Date,
  endDate: Date,
  periodLimitInDays: number,
): Date => {
  if (endDate <= startDate) {
    return startDate;
  }

  const maxEndDate = addDays(startDate, periodLimitInDays - 1);

  return endDate > maxEndDate ? maxEndDate : endDate;
};

export const useAbsenceApplicationForm = ({
  properties,
  onOverlap,
  onSuccess,
  onServerError,
}: Params) => {
  const { currentProfile } = useUserContext();
  const { progress: scrollProgress, handleScroll } = useScrollFadeProgress();
  const isMobile = useIsMobile();
  const [calendarField, setCalendarField] = useState<DateField | null>(null);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const reopenTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { mutateAsync: submitApplication, isPending } =
    useSubmitNonAttendanceApplication();

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      from: new Date(properties.validPeriod.from),
      to: null,
      cateringChoice: null,
      reason: '',
    },
  });

  const fromValue = useWatch({ control, name: DateField.From });
  const toValue = useWatch({ control, name: DateField.To });

  const calendarRange = useAbsenceCalendarRange({
    properties,
    calendarField,
    fromValue,
    toValue,
  });
  const profileClassAndSchool = joinNonEmpty([
    currentProfile?.user.className,
    currentProfile?.user.schoolName,
  ]);

  const closeCalendar = () => setCalendarField(null);

  const openCalendar = (field: DateField) => {
    if (reopenTimeoutRef.current) {
      clearTimeout(reopenTimeoutRef.current);
    }

    setCalendarField(null);
    reopenTimeoutRef.current = setTimeout(
      () => setCalendarField(field),
      CALENDAR_REOPEN_DELAY_MS,
    );
  };

  const selectCalendarDate = (date: Date) => {
    if (!calendarField) {
      return;
    }

    if (calendarField === DateField.From) {
      setValue(DateField.From, date, { shouldValidate: true });

      if (toValue) {
        setValue(
          DateField.To,
          normalizeEndDate(date, toValue, properties.periodLimitInDays),
          { shouldValidate: true },
        );
      }
    } else {
      setValue(DateField.To, date, { shouldValidate: true });
    }

    setCalendarField(null);
  };

  const submit = async (data: FormValues) => {
    if (!data.to) {
      return;
    }

    const payload: SubmitNonAttendanceApplicationRequest = {
      datePeriod: {
        from: toApiDateTime(data.from),
        to: toApiDateTime(data.to),
      },
      applicationText: data.reason,
    };

    if (properties.isCateringEnabled && data.cateringChoice) {
      payload.isCateringCanceled =
        data.cateringChoice === CateringChoice.NotNeeded;
    }

    try {
      const application = await submitApplication(payload);
      onSuccess(application, payload);
    } catch (error) {
      if (
        getResponseErrorCode(error) ===
        SubmitApplicationErrorCode.PeriodOverlaps
      ) {
        onOverlap();
        return;
      }

      logRequestError(ErrorMessage.SubmitNonAttendanceApplication, error);
      onServerError(payload);
    }
  };

  useEffect(() => {
    if (!calendarField || isMobile) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;

      if (!target || !datePickerRef.current) {
        return;
      }

      if (datePickerRef.current.contains(target)) {
        return;
      }

      setCalendarField(null);
    };

    document.addEventListener('click', handleClickOutside);

    return () => document.removeEventListener('click', handleClickOutside);
  }, [calendarField, isMobile]);

  useEffect(
    () => () => {
      if (reopenTimeoutRef.current) {
        clearTimeout(reopenTimeoutRef.current);
      }
    },
    [],
  );

  return {
    control,
    register,
    errors,
    isPending,
    currentProfile,
    profileClassAndSchool,
    scrollProgress,
    handleScroll,
    datePickerRef,
    calendarField,
    calendarRange,
    openCalendar,
    closeCalendar,
    selectCalendarDate,
    onSubmit: handleSubmit(submit),
  };
};
